// ** Root Slice & Helper Functions
import { baseApi } from './baseApi'
import { customErrorHandler } from './customErrorHandler'
import { TAGS } from './tags'

// ** Type Imports
import {
	type CompanyDetails,
	type CreateCompanyPayload,
	type CreateCompanyResponse,
	type UpdateCompanyPayload
} from '@/types'

/**
 * Company endpoints for the new company form (create + update).
 *
 * Endpoint names are prefixed (`companyCreate` / `companyUpdate` /
 * `companyGet`) because the legacy slices already own `updateCompany` and
 * `getCompany`; RTK Query endpoint names are global, so reusing them would
 * silently override the legacy endpoints.
 *
 * Tag ids use the company id as a number on both `providesTags` and
 * `invalidatesTags` — the legacy edit flow mismatched string/number ids and
 * never invalidated its cache (tracked as a known bug).
 */
const companyApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		companyCreate: build.mutation<CreateCompanyResponse, CreateCompanyPayload>({
			query: (data) => ({
				url: '/companies',
				method: 'POST',
				body: data
			}),
			transformErrorResponse: customErrorHandler,
			invalidatesTags: [TAGS.COMPANIES, TAGS.LOSS_LIMITS]
		}),
		companyUpdate: build.mutation<CompanyDetails, UpdateCompanyPayload>({
			query: ({ id, ...data }) => ({
				url: `/companies/${id}`,
				method: 'PUT',
				body: data
			}),
			transformErrorResponse: customErrorHandler,
			invalidatesTags: (_, __, { id }) => [
				TAGS.COMPANIES,
				TAGS.LOSS_LIMITS,
				{ type: TAGS.COMPANY_BUSINESS_INFO, id }
			]
		}),
		companyGet: build.query<CompanyDetails, { id: number }>({
			query: ({ id }) => ({
				url: `/companies/${id}`
			}),
			transformErrorResponse: customErrorHandler,
			providesTags: (_, __, { id }) => [
				{ type: TAGS.COMPANY_BUSINESS_INFO, id }
			]
		})
	}),
	overrideExisting: true
})

export const {
	useCompanyCreateMutation,
	useCompanyGetQuery,
	useCompanyUpdateMutation
} = companyApi
